import React, { useState, useEffect } from 'react';
import { Box, TextField } from '@mui/material';

import { createClassFromLiteSpec } from 'react-vega-lite';
import { getNrxGroupAdjustment, getNrxStats } from '../../../lib/api';
import { getFormattedStats, getAdjustmentPreview } from '../../../lib/charts';
import ApexChart from '../../../components/Chart';
import DraggableModal from '../../../components/DraggableModal';
import NrxGroupAdjustment from './NrxGroupAdjustment';
import { NRX_GROUP_BREAKS } from './constants';
import { getChartColors } from '../../../services/chart-colors';
import { usePermission } from '../../../hooks/use-permission';

const rgbaToHex = (rgba) => {
  const extractionRegEx = /\(([^)]+)\)/;
  const numbers = extractionRegEx.exec(rgba);
  if (!numbers[0]) return '#777777'; // Default to return gray.

  const trimmedNumbers = numbers[0].substring(1, numbers[0].length - 1);
  const splits = trimmedNumbers.split(',').map((sp) => sp.replace(/ /g, ''));
  const numericSplits = splits.map((numStr) => parseInt(numStr, 10));
  const [r, g, b] = numericSplits;

  let rHex = r.toString(16);
  rHex = rHex.length === 1 ? `0${rHex}` : rHex;
  let gHex = g.toString(16);
  gHex = gHex.length === 1 ? `0${gHex}` : gHex;
  let bHex = b.toString(16);
  bHex = bHex.length === 1 ? `0${bHex}` : bHex;

  return `#${rHex}${gHex}${bHex}`;
};

const getMapSpec = (attributeName, title, domain, range) => ({
  height: 200,
  width: 200,
  padding: 5,
  mark: {
    type: 'geoshape',
  },
  projection: {
    type: 'mercator',
  },
  encoding: {
    color: {
      type: 'quantitative',
      field: attributeName,
      title,
      scale: {
        domain,
        range,
        type: 'linear',
      },
    },
  },
});

const FieldDetails = ({
  field,
  savedAdjustmentFactor,
  handleClose,
  handleUpdateAdjustmentFactor,
}) => {
  const canAdjust = usePermission('RUN_RX');
  const chartColors = getChartColors('corn-nitrogen-rx');
  const [nrxStatsLoading, setNrxStatsLoading] = useState(false);
  const [nrxStats, setNrxStats] = useState(null);
  const [nrxBaseStats, setNrxBaseStats] = useState(null);
  const [nrxColors, setNrxColors] = useState([]);
  const [adjustmentFactor, setAdjustmentFactor] = useState('');
  const [groupAdjustmentFactors, setGroupAdjustmentFactors] = useState([]);
  const [nrxMapSpec, setNrxMapSpec] = useState(null);
  const [nrxMapData, setNrxMapData] = useState({
    values: {
      type: 'FeatureCollection',
      features: [],
    },
    format: {
      type: 'json',
      property: 'features',
    },
  });

  useEffect(() => {
    setNrxStatsLoading(true);
    getNrxStats(field.aaManagedId, true).then(async (stats) => {
      const groupAdjustments = await getNrxGroupAdjustment(field.aaManagedId);
      const currentValueMap = groupAdjustments.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.maxValue]: cur.adjustmentFactor,
        }),
        {},
      );
      const cArr = chartColors;
      setNrxBaseStats(stats);
      const groups = NRX_GROUP_BREAKS.map((groupBreak, idx, arr) => ({
        aaManagedId: field.aaManagedId,
        minValue: idx === 0 ? 0 : arr[idx - 1] + 1,
        maxValue: groupBreak,
        adjustmentFactor:
          savedAdjustmentFactor + (currentValueMap[groupBreak] || 0),
      }));

      setGroupAdjustmentFactors(groups);
      setNrxColors(cArr);
      setNrxStatsLoading(false);
      setAdjustmentFactor(savedAdjustmentFactor);
    });
  }, [field, savedAdjustmentFactor, chartColors]);

  useEffect(() => {
    if (!nrxColors) return;
    if (!nrxBaseStats) return;
    if (!groupAdjustmentFactors || groupAdjustmentFactors.length === 0) return;

    const previewStats = getAdjustmentPreview(
      nrxBaseStats,
      adjustmentFactor || 0,
      groupAdjustmentFactors,
      0,
      350,
      5,
      5,
    );
    const formattedStats = getFormattedStats(previewStats, nrxColors);

    setNrxStats(formattedStats);
  }, [nrxColors, nrxBaseStats, groupAdjustmentFactors, adjustmentFactor]);

  useEffect(() => {
    if (!nrxStats) return;

    // Update the nrx map spec when the stats change.
    const domain = nrxStats.chart.values.map((cv) => cv.label);
    const range = nrxStats.colors.map((c) => rgbaToHex(c));
    const mapSpec = getMapSpec(
      'properties.TGT_RATE',
      'Rate lbs/ac',
      domain,
      range,
    );

    setNrxMapSpec(mapSpec);
    setNrxMapData({
      values: {
        type: 'FeatureCollection',
        features: nrxStats.features,
      },
      format: {
        type: 'json',
        property: 'features',
      },
    });
  }, [nrxStats]);

  const revertStats = (adjustmentFactors) => () => {
    const clearedAdjustmentFactors = adjustmentFactors.map((af) => ({
      ...af,
      adjustmentFactor: 0,
    }));

    setAdjustmentFactor('0');
    setGroupAdjustmentFactors(clearedAdjustmentFactors);
  };

  const saveStats = () => {
    handleUpdateAdjustmentFactor(
      field.aaManagedId,
      parseFloat(adjustmentFactor),
      groupAdjustmentFactors,
    );
  };

  const NrxMap = nrxMapSpec ? createClassFromLiteSpec(nrxMapSpec) : null;
  return (
    <DraggableModal
      handleClose={handleClose}
      isLoading={nrxStatsLoading}
      title={`${field.fieldName} NRX Adjustment`}
    >
      <div style={{ width: '860px' }}>
        {!nrxStatsLoading && nrxStats && (
          <Box>
            <ApexChart
              xLabel="Rate (lbs/ac)"
              xUnits="lbs/ac"
              yLabel="Acres"
              stats={nrxStats}
              chartWidth="500px"
              mapComponent={
                nrxMapData && nrxMapSpec ? (
                  <NrxMap
                    style={{
                      paddingTop: '20px',
                    }}
                    data={nrxMapData}
                  />
                ) : null
              }
            />
            {canAdjust && (
              <div>
                <TextField
                  variant="standard"
                  label="Full Field Adjustment"
                  value={adjustmentFactor}
                  onChange={(e) => {
                    setAdjustmentFactor(e.target.value);

                    // Update all group adjustments.
                    const updatedGroupAdjustmentFactors =
                      groupAdjustmentFactors.map((gaf) => ({
                        aaManagedId: gaf.aaManagedId,
                        minValue: gaf.minValue,
                        maxValue: gaf.maxValue,
                        adjustmentFactor: e.target.value,
                      }));
                    setGroupAdjustmentFactors(updatedGroupAdjustmentFactors);
                  }}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                />

                <NrxGroupAdjustment
                  aaManagedId={field.aaManagedId}
                  currentValues={groupAdjustmentFactors}
                  onChange={(groupAdjustments) => {
                    setGroupAdjustmentFactors(groupAdjustments);
                  }}
                  onRevert={revertStats(groupAdjustmentFactors)}
                  onSave={saveStats}
                  onClose={handleClose}
                />
              </div>
            )}
          </Box>
        )}
      </div>
    </DraggableModal>
  );
};

export default FieldDetails;
